// In-app article store. Articles are CMS-style content pages — each
// "variant" is a slug + content shape that the Articles page knows how
// to render. NetSuite is a poor home for long-form copy, so once a CMS
// is wired up this becomes the fetch boundary.

export const ARTICLES = [
  {
    slug: "fitting-and-sizing",
    title: "Fitting & Sizing",
    eyebrow: "How To",
    summary:
      "A pack that fits is a pack you forget about. Here's how to size yourself, dial in your torso length, and put your harness where it belongs — on your hips.",
    sections: [
      {
        kind: "step",
        number: 1,
        title: "Measure your torso",
        body:
          "Find your C7 vertebra (the bony bump where your neck meets your shoulders) and measure straight down to the top of your iliac crest — the shelf at the top of your hip bones. That distance is your torso length.",
        callout: "Most adults land between 16″ and 21″. Don't guess; measure.",
      },
      {
        kind: "step",
        number: 2,
        title: "Pick the right size",
        body:
          "Match your torso measurement to the size chart on each product page. If you're between sizes, size down — a slightly short pack rides better than one that's too long.",
        sizeChart: [
          { size: "S",  range: '15"–17"', cm: "38–43 cm" },
          { size: "M",  range: '17"–19"', cm: "43–48 cm" },
          { size: "L",  range: '19"–21"', cm: "48–53 cm" },
          { size: "XL", range: '21"+',    cm: "53+ cm" },
        ],
      },
      {
        kind: "step",
        number: 3,
        title: "Set the hip belt first",
        body:
          "Loosen every strap. Cinch the hip belt over your iliac crest — the belt should sit on your hip bones, not your waist. Tighten until it carries the weight off your shoulders.",
      },
      {
        kind: "step",
        number: 4,
        title: "Snug the shoulder straps",
        body:
          "Pull the shoulder straps until the pack body sits flush against your back. The straps should anchor at the top of your shoulders, not climb your neck.",
      },
      {
        kind: "step",
        number: 5,
        title: "Tune the load lifters",
        body:
          "Pull the load lifters at the top of the shoulder straps until they make a 30°–45° angle. This pulls the pack into your back and transfers more of the load to your hips.",
        callout: "If you can't get a 30° angle, the torso is too long; size down.",
      },
      {
        kind: "step",
        number: 6,
        title: "Lock in the sternum strap",
        body:
          "Click in the sternum strap and snug it just enough to keep the shoulder straps from slipping outward. Don't over-tighten — it shouldn't restrict breathing.",
      },
    ],
    tips: [
      "Re-check the fit after 10 minutes on the trail. Loaded straps settle.",
      "Loosen everything before each adjustment; don't fight stacked tension.",
      "If your shoulders ache, the hip belt isn't carrying enough — re-snug from the bottom up.",
    ],
  },
];

ARTICLES.push({
  slug: "patents",
  title: "Patents",
  eyebrow: "Legal",
  summary:
    "The following Mystery Ranch products are protected by patents in the U.S. and elsewhere. This page satisfies the virtual patent marking provisions of the America Invents Act and other jurisdictions.",
  sections: [
    {
      kind: "step",
      number: 1,
      title: "US 8,162,194 B2 · US 8,955,729 B",
      body:
        "6500 · 3 Day Assault BVS · Blackjack 50 / 80 / 100 · COMM 3 · Jump Mountain Ruck · Load Sling · Mongoose · Mountain Ruck · NICE Frame · RATS variants · ROUS · SATL · SPEAR series · Tactiplane BVS · VROD.",
    },
    {
      kind: "step",
      number: 2,
      title: "US 7,673,777 B2 · US 8,381,956 B · US 8,561,866 B · US 8,950,644 B2",
      body:
        "6500 · Blackjack series · Cabinet · COMM 3 · Guide Light Frame · Marshall · Metcalf · Mongoose · Mountain Ruck · NICE Frame · Overload · Pintler · SPEAR series · VROD.",
    },
    {
      kind: "step",
      number: 3,
      title: "US 8,348,114 B2 · US 8,579,171 B",
      body:
        "Cabinet · Guide Light Frame · Marshall · Metcalf · Mule variants · Pop-Up models · Selway 60 · Terraframe models · Women's options.",
    },
    {
      kind: "step",
      number: 4,
      title: "US 7,673,777 B2 · US 8,381,956 B · US 8,561,866 B · US 8,950,644 B (extended list)",
      body:
        "1/3 Day Assault variants · Blackjack series · Cairn · Coulee variants · Crest · DATL · Divide · EX models · Flume · Glacier variants · Hot variants · Hover Pack · Jump models · Komodo Dragon · Marshall · Metcalf · Mountain Ruck · MSOB · Mule · Mystic · Patrol variants · Pintler · Pop-Up models · RATS variants · Ridge Ruck models · ROUS · Saddle Peak · Scapegoat variants · Selway 60 · Sphinx · Stein variants · Terraframe models · Terraplane variants · THOR · Trail Yoke · Treehouse · VROD · Women's variants.",
    },
    {
      kind: "step",
      number: 5,
      title: "US 6,626,342 B1",
      body:
        "6500 · Blackjack series · Bomb Pack USA · Cabinet · Divide · Glacier variants · Guide Light Frame · Hot models · Jump Mountain Ruck · Marshall · Metcalf · Mule · NICE Frame · Overload · Patrol variants · Pintler · Pop-Up models · RATS variants · ROUS · Selway 60 · SPEAR series · Sphinx · Stein variants · Terraframe / Terraplane variants · Treehouse · VROD · Women's variants.",
    },
    {
      kind: "step",
      number: 6,
      title: "US 9,173,473 B2",
      body: "MSOB · Mystery Cinch.",
    },
    {
      kind: "step",
      number: 7,
      title: "US 9,271,559 B2",
      body: "BASE Frame.",
    },
    {
      kind: "step",
      number: 8,
      title: "US 10,952,523",
      body: "Pop Up 28 · Women's Pop Up 28 · Pop Up 38.",
    },
  ],
  tips: [
    "This list may be incomplete — additional products may also carry patent protection.",
    "Patents apply in the U.S. and in other jurisdictions where Mystery Ranch holds rights.",
  ],
});

export function findArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}
